<template>
  <div class="relative" :class="'field' + name">
    <input
      ref="addressInputRef"
      v-model="internalValue"
      type="text"
      :name="name"
      :placeholder="placeholder"
      class="w-full border rounded-small shadow-gray pt-4 pb-[13px] px-[18px] text-base sm:text-xl border-charcoal placeholder:text-silver h-[49px] sm:h-[53px]"
      autocomplete="new-password"
      @focus="focusField"
      @blur="focusField"
      @keydown.enter.prevent
    />
    <Field
      :name="name"
      :rules="rules"
      v-model="internalValue"
      v-slot="{ field, errorMessage: fieldError }"
    >
      <input type="hidden" v-bind="field" />
    </Field>
    <span
      :class="isFocused ? 'opacity-100 z-[1]' : ' opacity-0 z-0'"
      class="absolute bg-white -top-2 left-4 px-1 transition-all duration-75 ease-linear text-sm"
    >{{ placeholder }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, type Ref, inject } from 'vue'
import { defineRule, Field, useField } from 'vee-validate'
import { required } from '@utills/helpers/validation'
import { useGooglePlacesAutocomplete } from '@/composables/useGooglePlacesAutocomplete'

export default defineComponent({
  components: { Field },
  props: {
    name: {
      type: String,
      required: true
    },
    rules: {
      type: Object,
      required: true
    },
    placeholder: {
      type: String,
      required: true
    },
    modelValue: {
      type: String,
      default: ''
    },
    // Field names for auto-population
    cityFieldName: {
      type: String,
      required: true
    },
    stateFieldName: {
      type: String,
      required: true
    },
    zipFieldName: {
      type: String,
      required: true
    }
  },
  setup(props, { emit }) {
    defineRule('required', required)
    const { errorMessage, setValue } = useField(props.name, props.rules)
    const addressInputRef = ref<HTMLInputElement | null>(null)
    const isFocused = ref(false)

    // Get the form context from vee-validate
    const form = inject<any>('veeForm', null)

    const internalValue = computed({
      get: () => props.modelValue,
      set: (val) => {
        setValue(val)
        emit('update:modelValue', val)
      }
    })

    function focusField() {
      isFocused.value = !isFocused.value
    }

    // Initialize Google Places Autocomplete
    useGooglePlacesAutocomplete({
      inputRef: addressInputRef as Ref<HTMLInputElement | null>,
      onPlaceSelected: (addressComponents) => {
        console.log('onPlaceSelected callback triggered')
        console.log('Address components received:', addressComponents)
        console.log('Field names - City:', props.cityFieldName, 'State:', props.stateFieldName, 'Zip:', props.zipFieldName)

        // Build the full street address
        const streetAddress = [
          addressComponents.street_number,
          addressComponents.route
        ]
          .filter(Boolean)
          .join(' ')

        console.log('Street address built:', streetAddress)

        // Use setTimeout to ensure the autocomplete has finished updating the input
        setTimeout(() => {
          // Update the address field with ONLY the street address
          if (streetAddress) {
            internalValue.value = streetAddress
            // Also update the actual input element to override autocomplete's value
            if (addressInputRef.value) {
              addressInputRef.value.value = streetAddress
            }
            console.log('Address field updated to:', streetAddress)
          }

          // Fallback: Always use DOM manipulation as it's more reliable
          const updateFieldByName = (fieldName: string, value: string) => {
            console.log(`Attempting to update field "${fieldName}" with value "${value}"`)
            const field = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement
            if (field) {
              field.value = value
              // Trigger input event to update vee-validate
              field.dispatchEvent(new Event('input', { bubbles: true }))
              // Also trigger change event
              field.dispatchEvent(new Event('change', { bubbles: true }))
              console.log(`Successfully updated field "${fieldName}"`)
            } else {
              console.warn(`Field "${fieldName}" not found in DOM`)
            }
          }

          // Update city field
          if (addressComponents.locality) {
            updateFieldByName(props.cityFieldName, addressComponents.locality)
            // Also try form context if available
            if (form && form.setFieldValue) {
              form.setFieldValue(props.cityFieldName, addressComponents.locality)
            }
          }

          // Update state field
          if (addressComponents.administrative_area_level_1) {
            updateFieldByName(props.stateFieldName, addressComponents.administrative_area_level_1)
            if (form && form.setFieldValue) {
              form.setFieldValue(props.stateFieldName, addressComponents.administrative_area_level_1)
            }
          }

          // Update zip code field
          if (addressComponents.postal_code) {
            updateFieldByName(props.zipFieldName, addressComponents.postal_code)
            if (form && form.setFieldValue) {
              form.setFieldValue(props.zipFieldName, addressComponents.postal_code)
            }
          }

          console.log('All fields update attempted')
        }, 50)
      }
    })

    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue !== internalValue.value) {
          internalValue.value = newValue
        }
      }
    )

    return {
      internalValue,
      isFocused,
      focusField,
      errorMessage,
      addressInputRef
    }
  }
})
</script>


